/* eslint-disable deprecation/deprecation */
import {
  CardActionsProps as MuiCardActionProps,
  CardActions as MuiCardActions,
  paperClasses,
  SxProps,
  Theme,
  useThemeProps,
} from '@mui/material';
import React from 'react';
import {
  cardContentHorizontalThemeSpacing,
  cardContentVerticalThemeSpacing,
} from 'src/components/CardContent/cardContentStyleOverrides';
import { getToken } from '@workiva/unify-tokens';

// Add new props showDividers and disableTopSpacing
export interface CardActionsProps
  extends Omit<MuiCardActionProps, 'disableSpacing'> {
  /**
   * If `true`, the actions do not have additional margin.
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  disableSpacing?: MuiCardActionProps['disableSpacing'];
  /**
   * Whether a divider should be shown between the action and the adjacent `CardContent` areas.
   *
   * Defaults to `true` when the parent `Card` is an outlined variant, otherwise `false`.
   */
  showDividers?: boolean;

  /**
   * Set to remove the top spacing.
   * @default false
   */
  disableTopSpacing?: boolean;
}

/**
 * CardActions contains the action buttons at the bottom of a Card.
 *
 * Demos:
 *
 * - [Card — Actions](https://unify.workiva.org/components/card?tab=code#card-actions)
 *
 * API:
 *
 * - [CardActions API](https://unify.workiva.org/components/card?tab=props#cardactions)
 */
export const CardActions = React.forwardRef<HTMLElement, CardActionsProps>(
  function UnifyCardActions(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiCardActions' });

    const {
      showDividers,
      disableTopSpacing,
      children,
      sx: inSx = {},
      ...rest
    } = props;

    // Build custom handling of the borders. Apply a top and bottom border in every case except the last entry.
    const borderStyles: Partial<SxProps<Theme>> = {
      borderTop: `1px solid ${getToken('Core/Layer/layer-divider')}`,
      // As long as the action is not (:not()) the last child (:last-child) of the parent (&) render a bottom border.
      '&:not(:last-child)': {
        borderBottom: `1px solid ${getToken('Core/Layer/layer-divider')}`,
      },
    };

    // Apply style overrides to the `sx` props.
    const sx: SxProps<Theme> = {
      // Adjust padding by lr: 2 & tb: 3 theme spacing units.
      padding: (theme) =>
        theme.spacing(
          disableTopSpacing ? 0 : cardContentVerticalThemeSpacing,
          cardContentHorizontalThemeSpacing,
          cardContentVerticalThemeSpacing,
          cardContentHorizontalThemeSpacing
        ),
      // Skip applying the divider if the flag is unset.
      ...(showDividers !== false &&
        !disableTopSpacing && {
          // Apply borders in an outlined parent by default.
          [`.${paperClasses.outlined} &`]: borderStyles,
        }),
      // Force apply border if set.
      ...(showDividers && !disableTopSpacing && borderStyles),
      // Propagate the custom styles.
      ...inSx,
    };

    return (
      <MuiCardActions ref={ref} sx={sx} {...rest}>
        {children}
      </MuiCardActions>
    );
  }
);

export default CardActions;
